import api from './api';
import type { ApiResponse } from '@/types';

/**
 * Phase 80 (CCR-03) — CallOutcome disposition catalog (behind
 * `feature:call_center_reporting_enabled`, 404 when the flag is off):
 *
 *   GET    /call-outcomes             (calls.view — modal picker + settings list read)
 *   POST   /call-outcomes             (calls.settings — admin-only catalog write)
 *   PUT    /call-outcomes/{id}        (calls.settings — no `code`, immutable after create)
 *   DELETE /call-outcomes/{id}        (calls.settings)
 *   POST   /call-outcomes/reorder     (calls.settings)
 *
 * The post-call disposition modal (components/telephony/post-call-disposition-modal.tsx)
 * and the settings vertical (components/settings/call-outcomes-settings.tsx) both import
 * from here — this module never re-declares an endpoint.
 */

export interface CallOutcome {
  id: string;
  name: string;
  code: string;
  color: string;
  order: number;
  is_active: boolean;
}

export interface CreateCallOutcomeData {
  name: string;
  code: string;
  color: string;
  order?: number;
  is_active?: boolean;
}

// NOTE: `code` deliberately absent — immutable after create (backend silently
// drops it on PUT; the type mirrors that contract so no caller can attempt it).
export interface UpdateCallOutcomeData {
  name?: string;
  color?: string;
  order?: number;
  is_active?: boolean;
}

export const getCallOutcomes = async (): Promise<CallOutcome[]> => {
  const response = await api.get<ApiResponse<CallOutcome[]>>('/call-outcomes');
  return response.data.data;
};

export const createCallOutcome = async (data: CreateCallOutcomeData): Promise<CallOutcome> => {
  const response = await api.post<ApiResponse<CallOutcome>>('/call-outcomes', data);
  return response.data.data;
};

export const updateCallOutcome = async (id: string, data: UpdateCallOutcomeData): Promise<CallOutcome> => {
  const response = await api.put<ApiResponse<CallOutcome>>(`/call-outcomes/${id}`, data);
  return response.data.data;
};

export const deleteCallOutcome = async (id: string): Promise<void> => {
  await api.delete(`/call-outcomes/${id}`);
};

/** POST .../reorder returns only a message (no data) — refetch to repopulate. */
export const reorderCallOutcomes = async (order: string[]): Promise<CallOutcome[]> => {
  await api.post('/call-outcomes/reorder', { order });
  return getCallOutcomes();
};
